/**
 * Circular glass video control with progress ring (play / pause / restart).
 * 圆形毛玻璃视频控件：外圈进度环，按状态显示播放 / 暂停 / 重播。
 * Progress follows real media time and freezes during network buffering.
 * 进度跟随真实播放时间，网络缓冲卡顿时冻结，避免与画面脱节。
 */
(function () {
  var RING_LENGTH = 2 * Math.PI * 18; // r=18 in 44x44 viewBox
  var HAVE_CURRENT_DATA = 2;

  function resolveVideo(btn) {
    var rootSelector = btn.getAttribute('data-vc-root');
    var root = rootSelector ? document.querySelector(rootSelector) : document;
    if (!root) root = document;

    var groupSelector = btn.getAttribute('data-vc-group');
    if (groupSelector) {
      var activeClass = btn.getAttribute('data-vc-active-class') || 'is-active';
      var groupVideos = root.querySelectorAll(groupSelector);
      for (var i = 0; i < groupVideos.length; i++) {
        if (groupVideos[i].classList.contains(activeClass)) return groupVideos[i];
      }
      return groupVideos[0] || null;
    }

    var videoSelector = btn.getAttribute('data-vc-video');
    if (!videoSelector) return null;
    return root.querySelector(videoSelector);
  }

  function setState(btn, state) {
    btn.setAttribute('data-state', state);
    var labels = {
      playing: 'Pause video',
      paused: 'Play video',
      ended: 'Restart video'
    };
    btn.setAttribute('aria-label', labels[state] || 'Play video');
  }

  function updateProgress(btn, video) {
    var progressEl = btn.querySelector('.vc-btn__ring-progress');
    if (!progressEl || !video || !isFinite(video.duration) || video.duration <= 0) {
      if (progressEl) progressEl.style.strokeDashoffset = String(RING_LENGTH);
      return;
    }
    var ratio = Math.min(1, Math.max(0, video.currentTime / video.duration));
    progressEl.style.strokeDashoffset = String(RING_LENGTH * (1 - ratio));
  }

  /** True when the element intends to play (not user-paused / ended). */
  function wantsToPlay(video) {
    return !!(video && !video.paused && !video.ended);
  }

  /**
   * True when frames are actually advancing (not buffering / seeking).
   * Network stalls keep paused=false, so play-intent alone is not enough.
   */
  function isActivelyPlaying(video) {
    return (
      wantsToPlay(video) &&
      !video.seeking &&
      video.readyState >= HAVE_CURRENT_DATA
    );
  }

  function syncFromVideo(btn, video) {
    if (!video) {
      setState(btn, 'paused');
      return;
    }
    if (video.ended) {
      setState(btn, 'ended');
    } else if (wantsToPlay(video)) {
      // Keep "playing" icon during buffer waits so restart is not shown mid-stall.
      setState(btn, 'playing');
    } else {
      setState(btn, 'paused');
    }
    updateProgress(btn, video);
  }

  function bindControl(btn) {
    if (btn.dataset.vcBound === 'true') return;
    btn.dataset.vcBound = 'true';

    var progressEl = btn.querySelector('.vc-btn__ring-progress');
    if (progressEl) {
      progressEl.style.strokeDasharray = String(RING_LENGTH);
      progressEl.style.strokeDashoffset = String(RING_LENGTH);
    }

    var activeVideo = null;
    var rafId = 0;
    var lastMediaTime = -1;
    var stallFrames = 0;
    var STALL_FRAME_LIMIT = 12; // ~200ms at 60fps with no currentTime change

    function stopRaf() {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      stallFrames = 0;
    }

    function startRaf() {
      stopRaf();
      if (!activeVideo) return;
      lastMediaTime = activeVideo.currentTime;
      rafId = requestAnimationFrame(tick);
    }

    function tick() {
      if (!activeVideo) {
        rafId = 0;
        return;
      }

      var mediaTime = activeVideo.currentTime;
      if (mediaTime !== lastMediaTime) {
        lastMediaTime = mediaTime;
        stallFrames = 0;
        updateProgress(btn, activeVideo);
      } else if (wantsToPlay(activeVideo)) {
        stallFrames += 1;
      }

      // Stop the ring while buffering: paused stays false on waiting/stalled.
      if (!isActivelyPlaying(activeVideo) || stallFrames >= STALL_FRAME_LIMIT) {
        rafId = 0;
        updateProgress(btn, activeVideo);
        return;
      }

      rafId = requestAnimationFrame(tick);
    }

    function onPlay() {
      setState(btn, 'playing');
      // May still be buffering; ring starts on `playing` / resumes below.
      if (isActivelyPlaying(activeVideo)) startRaf();
    }

    function onPlaying() {
      setState(btn, 'playing');
      startRaf();
    }

    function onWaiting() {
      stopRaf();
      updateProgress(btn, activeVideo);
      if (wantsToPlay(activeVideo)) setState(btn, 'playing');
    }

    function onStalled() {
      stopRaf();
      updateProgress(btn, activeVideo);
    }

    function onPause() {
      if (activeVideo && activeVideo.ended) return;
      setState(btn, 'paused');
      stopRaf();
      updateProgress(btn, activeVideo);
    }

    function onEnded() {
      if (activeVideo && activeVideo.loop) {
        setState(btn, 'playing');
        updateProgress(btn, activeVideo);
        startRaf();
        return;
      }
      setState(btn, 'ended');
      stopRaf();
      updateProgress(btn, activeVideo);
    }

    function onTimeUpdate() {
      updateProgress(btn, activeVideo);
      // Some browsers skip a second `playing` after `waiting`; resume the ring here.
      if (!rafId && isActivelyPlaying(activeVideo)) startRaf();
    }

    function detach(video) {
      if (!video) return;
      video.removeEventListener('play', onPlay);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onMeta);
    }

    function onMeta() {
      updateProgress(btn, activeVideo);
      syncFromVideo(btn, activeVideo);
    }

    function attach(video) {
      if (!video || video === activeVideo) {
        syncFromVideo(btn, video);
        if (video && isActivelyPlaying(video) && !rafId) startRaf();
        return;
      }
      detach(activeVideo);
      stopRaf();
      activeVideo = video;
      activeVideo.addEventListener('play', onPlay);
      activeVideo.addEventListener('playing', onPlaying);
      activeVideo.addEventListener('waiting', onWaiting);
      activeVideo.addEventListener('stalled', onStalled);
      activeVideo.addEventListener('pause', onPause);
      activeVideo.addEventListener('ended', onEnded);
      activeVideo.addEventListener('timeupdate', onTimeUpdate);
      activeVideo.addEventListener('loadedmetadata', onMeta);
      syncFromVideo(btn, activeVideo);
      if (isActivelyPlaying(activeVideo)) startRaf();
    }

    function refresh() {
      attach(resolveVideo(btn));
    }

    btn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      refresh();
      if (!activeVideo) return;

      if (activeVideo.ended || btn.getAttribute('data-state') === 'ended') {
        try {
          activeVideo.currentTime = 0;
        } catch (e) {}
        var restartPromise = activeVideo.play();
        if (restartPromise && typeof restartPromise.catch === 'function') {
          restartPromise.catch(function () {
            setState(btn, 'ended');
          });
        }
        return;
      }

      if (activeVideo.paused) {
        var playPromise = activeVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            setState(btn, 'paused');
          });
        }
      } else {
        activeVideo.pause();
      }
    });

    refresh();

    // Keep group controls in sync when the active video class changes.
    var groupSelector = btn.getAttribute('data-vc-group');
    if (groupSelector) {
      var rootSelector = btn.getAttribute('data-vc-root');
      var root = rootSelector ? document.querySelector(rootSelector) : document;
      var groupVideos = root ? root.querySelectorAll(groupSelector) : [];
      if (typeof MutationObserver !== 'undefined') {
        var observer = new MutationObserver(refresh);
        for (var i = 0; i < groupVideos.length; i++) {
          observer.observe(groupVideos[i], { attributes: true, attributeFilter: ['class'] });
        }
      }
    }

    // Re-bind when a source finishes loading later (lazy sections).
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) refresh();
    });
  }

  function initAll() {
    var buttons = document.querySelectorAll('[data-vc-btn]');
    for (var i = 0; i < buttons.length; i++) {
      bindControl(buttons[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.BirdieVideoCircleControl = { init: initAll };
})();
